import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, ArrowLeft, Truck, Package, MapPin, Phone, User, Mail, CheckCircle, Loader2, Plus, Minus, X, ChevronRight, Shield, CreditCard, Lock, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TURKISH_CITIES = [
  'Adana', 'Adıyaman', 'Afyonkarahisar', 'Ağrı', 'Aksaray', 'Amasya', 'Ankara', 'Antalya', 'Ardahan', 'Artvin',
  'Aydın', 'Balıkesir', 'Bartın', 'Batman', 'Bayburt', 'Bilecik', 'Bingöl', 'Bitlis', 'Bolu', 'Burdur',
  'Bursa', 'Çanakkale', 'Çankırı', 'Çorum', 'Denizli', 'Diyarbakır', 'Düzce', 'Edirne', 'Elazığ', 'Erzincan',
  'Erzurum', 'Eskişehir', 'Gaziantep', 'Giresun', 'Gümüşhane', 'Hakkari', 'Hatay', 'Iğdır', 'Isparta', 'İstanbul',
  'İzmir', 'Kahramanmaraş', 'Karabük', 'Karaman', 'Kars', 'Kastamonu', 'Kayseri', 'Kırıkkale', 'Kırklareli', 'Kırşehir',
  'Kilis', 'Kocaeli', 'Konya', 'Kütahya', 'Malatya', 'Manisa', 'Mardin', 'Mersin', 'Muğla', 'Muş',
  'Nevşehir', 'Niğde', 'Ordu', 'Osmaniye', 'Rize', 'Sakarya', 'Samsun', 'Siirt', 'Sinop', 'Sivas',
  'Şanlıurfa', 'Şırnak', 'Tekirdağ', 'Tokat', 'Trabzon', 'Tunceli', 'Uşak', 'Van', 'Yalova', 'Yozgat', 'Zonguldak'
];

const INSTALLMENT_OPTIONS = [
  { count: 1, label: "Tek Çekim", rate: 0 },
  { count: 2, label: "2 Taksit", rate: 7 },
  { count: 3, label: "3 Taksit", rate: 8.64 },
  { count: 4, label: "4 Taksit", rate: 10.64 },
  { count: 5, label: "5 Taksit", rate: 12.97 },
  { count: 6, label: "6 Taksit", rate: 15.41 },
  { count: 7, label: "7 Taksit", rate: 17.95 },
  { count: 8, label: "8 Taksit", rate: 20.61 },
  { count: 9, label: "9 Taksit", rate: 23.4 },
  { count: 10, label: "10 Taksit", rate: 26.31 },
  { count: 11, label: "11 Taksit", rate: 29.37 },
  { count: 12, label: "12 Taksit", rate: 32.57 },
];

const GuestCheckoutPage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem('pazarlo_guest_cart');
    return saved ? JSON.parse(saved) : [];
  });
  const [step, setStep] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const savedCart = localStorage.getItem('pazarlo_guest_cart');
    const hasCartItems = savedCart && JSON.parse(savedCart).length > 0;
    if (urlParams.get('cart') === 'true' && hasCartItems) {
      return 2;
    }
    return 1;
  });
  const [orderComplete, setOrderComplete] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('credit_card');
  const [selectedInstallment, setSelectedInstallment] = useState(1);
  const [cardData, setCardData] = useState({
    cardHolderName: '',
    cardNumber: '',
    expMonth: '',
    expYear: '',
    cvv: '',
    use3D: true
  });
  
  const [form, setForm] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    shipping_address: '',
    shipping_city: 'İstanbul',
    shipping_district: '',
    note: ''
  });
  
  const [errors, setErrors] = useState({});

  useEffect(() => {
    localStorage.setItem("pazarlo_guest_cart", JSON.stringify(cart));
    fetchProducts();
  }, []);

  const fetchProducts = async (search = '') => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      params.append('limit', '20');
      
      const response = await axios.get(`${API}/guest/products?${params}`);
      setProducts(response.data.products || []);
    } catch (err) {
      console.error('Failed to fetch products:', err);
      toast.error('Ürünler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProducts(searchQuery);
  };

  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item =>
          item.id === product.id
            ? { ...item, quantity: Math.min(item.quantity + 1, product.stock || 99) }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
    toast.success('Sepete eklendi');
  };

  const updateQuantity = (productId, delta) => {
    setCart(prev =>
      prev.map(item => {
        if (item.id === productId) {
          const newQty = item.quantity + delta;
          if (newQty <= 0) return null;
          return { ...item, quantity: Math.min(newQty, item.stock || 99) };
        }
        return item;
      }).filter(Boolean)
    );
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.id !== productId));
  };

  const getPrice = (item) => item.display_price || item.price || item.base_price || 0;
  const getName = (item) => item.name || item.title || '';
  
  const cartTotal = cart.reduce((sum, item) => sum + getPrice(item) * item.quantity, 0);
  const [shippingData, setShippingData] = useState({ cargo_price_with_kdv: 0, is_free_shipping: false, calculation_type: 'loading' });
  const shippingCost = shippingData.cargo_price_with_kdv || 0;
  const grandTotal = cartTotal + shippingCost;

  // Kargo ücreti hesaplama
  useEffect(() => {
    const calculateShipping = async () => {
      if (cartTotal <= 0) {
        setShippingData({ cargo_price_with_kdv: 0, is_free_shipping: true, calculation_type: "empty" });
        return;
      }
      try {
        const totalDesi = cart.reduce((sum, item) => sum + (item.desi || 1) * item.quantity, 0);
        const response = await axios.post(`${API}/guest/calculate-cargo?order_amount=${cartTotal}&desi=${totalDesi}`);
        setShippingData(response.data);
      } catch (err) {
        console.error("Kargo hesaplama hatası:", err);
        setShippingData({ cargo_price_with_kdv: cartTotal >= 500 ? 0 : 49.17, is_free_shipping: cartTotal >= 500, calculation_type: "fallback" });
      }
    };
    calculateShipping();
  }, [cartTotal, cart]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 12 }, (_, i) => currentYear + i);
  const months = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));

  const getInstallmentDetails = (count) => {
    const option = INSTALLMENT_OPTIONS.find(o => o.count === count);
    if (!option) return { total: grandTotal, monthly: grandTotal, rate: 0 };
    const total = grandTotal * (1 + option.rate / 100);
    const monthly = total / count;
    return { total, monthly, rate: option.rate };
  };

  const currentInstallment = getInstallmentDetails(selectedInstallment);

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    return parts.length ? parts.join(' ') : value;
  };

  const handleCardNumberChange = (e) => {
    const formatted = formatCardNumber(e.target.value);
    if (formatted.replace(/\s/g, '').length <= 16) {
      setCardData({ ...cardData, cardNumber: formatted });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!form.customer_name.trim()) newErrors.customer_name = 'Ad soyad gerekli';
    if (!form.customer_phone.trim()) newErrors.customer_phone = 'Telefon gerekli';
    if (!form.shipping_address.trim()) newErrors.shipping_address = 'Adres gerekli';
    if (!form.shipping_city) newErrors.shipping_city = 'İl seçimi gerekli';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Lütfen zorunlu alanları doldurun');
      return;
    }

    if (paymentMethod === 'credit_card') {
      if (!cardData.cardHolderName || !cardData.cardNumber || !cardData.expMonth || !cardData.expYear || !cardData.cvv) {
        toast.error('Lütfen tüm kart bilgilerini doldurun');
        return;
      }
      const cardNum = cardData.cardNumber.replace(/\s/g, '');
      if (cardNum.length < 15) {
        toast.error('Geçersiz kart numarası');
        return;
      }
      if (cardData.cvv.length < 3) {
        toast.error('Geçersiz CVV');
        return;
      }
    }
    
    setSubmitting(true);
    try {
      const checkoutData = {
        ...form,
        payment_method: paymentMethod,
        items: cart.map(item => ({ listing_id: item.id, seller_listing_id: item.seller_listing_id || item.id, seller_id: item.seller_id, quantity: item.quantity }))
      };

      if (paymentMethod === 'credit_card') {
        const response = await axios.post(`${API}/guest/checkout`, checkoutData);
        const orderNumber = response.data.order_number;
        
        const cardNum = cardData.cardNumber.replace(/\s/g, '');
        const paymentResponse = await axios.post(`${API}/payment/process`, {
          order_number: orderNumber,
          installment_count: selectedInstallment,
          total_amount: currentInstallment.total,
          is_guest: true
        });

        if (paymentResponse.data.success) {
          if (paymentResponse.data.redirect_url) {
            window.location.href = paymentResponse.data.redirect_url;
          } else {
            setOrderComplete({ ...response.data, payment_method: 'credit_card' });
            setStep(3);
            localStorage.removeItem('pazarlo_guest_cart');
            setCart([]);
            toast.success('Ödeme başarılı!');
          }
        } else {
          toast.error(paymentResponse.data.message || 'Ödeme başarısız');
        }
      } else {
        const response = await axios.post(`${API}/guest/checkout`, checkoutData);
        setOrderComplete({ ...response.data, payment_method: 'kapida_odeme' });
        setStep(3);
        localStorage.removeItem('pazarlo_guest_cart');
        setCart([]);
        toast.success('Siparişiniz alındı!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sipariş oluşturulamadı');
    } finally {
      setSubmitting(false);
    }
  };

  // Order Complete Screen
  if (step === 3 && orderComplete) {
    return (
      <div className="min-h-screen bg-slate-50">
        <header className="bg-white border-b shadow-sm">
          <div className="container mx-auto px-4 py-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">P</span>
              </div>
              <span className="text-xl font-bold text-slate-800">Pazarlo</span>
            </Link>
          </div>
        </header>

        <div className="container mx-auto px-4 py-16 flex items-center justify-center">
          <Card className="max-w-lg w-full shadow-lg">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-800 mb-2">
                {orderComplete.payment_method === 'credit_card' ? 'Ödeme Başarılı!' : 'Siparişiniz Alındı!'}
              </h1>
              <p className="text-slate-500 mb-6">En kısa sürede hazırlanacaktır.</p>
              
              <div className="bg-slate-100 rounded-lg p-4 mb-6 text-left">
                <div className="flex justify-between mb-2">
                  <span className="text-slate-500">Sipariş No:</span>
                  <span className="font-mono font-semibold text-orange-600">{orderComplete.order_number}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-slate-500">Toplam:</span>
                  <span className="text-orange-600 font-semibold">{formatCurrency(orderComplete.grand_total)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Ödeme:</span>
                  <span>{orderComplete.payment_method === 'credit_card' ? 'Kredi Kartı' : 'Kapıda Ödeme'}</span>
                </div>
              </div>
              
              <p className="text-slate-400 text-sm mb-6">Tahmini teslimat: 3-5 iş günü</p>
              
              <div className="space-y-3">
                <Button onClick={() => navigate('/')} className="w-full bg-orange-500 hover:bg-orange-600">
                  Ana Sayfaya Dön
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold">P</span>
              </div>
              <span className="text-lg font-bold text-slate-800 hidden sm:block">Pazarlo</span>
            </Link>
            <div className="flex-1 text-center">
              <span className="text-sm text-slate-500">Oto Yedek Parça Pazarı</span>
            </div>
            <div className="relative">
              <ShoppingCart className="h-6 w-6 text-slate-600" />
              {cart.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {cart.length}
                </span>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Trust badges */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white py-2 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-6 text-xs md:text-sm">
            <div className="flex items-center gap-1">
              <Truck className="h-4 w-4" />
              <span>500₺ üzeri ücretsiz kargo</span>
            </div>
            <div className="flex items-center gap-1">
              <Shield className="h-4 w-4" />
              <span>Güvenli alışveriş</span>
            </div>
            <div className="flex items-center gap-1 hidden sm:flex">
              <Package className="h-4 w-4" />
              <span>Kapıda ödeme</span>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-slate-500 mb-6">
          <Link to="/" className="hover:text-orange-500">Ana Sayfa</Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-orange-500 font-medium">Misafir Alışveriş</span>
        </nav>

        {step === 1 ? (
          <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 pb-28 lg:pb-0">
            {/* Products */}
            <div className="lg:col-span-2">
              <Card className="shadow-sm">
                <CardHeader className="border-b bg-slate-50">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Search className="h-5 w-5 text-orange-500" />
                    Ürün Seçin
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <form onSubmit={handleSearch} className="mb-4">
                    <div className="flex gap-2">
                      <Input
                        placeholder="Ürün adı veya OEM numarası..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="flex-1"
                      />
                      <Button type="submit" className="bg-orange-500 hover:bg-orange-600">
                        <Search className="h-4 w-4" />
                      </Button>
                    </div>
                  </form>

                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[500px] overflow-y-auto">
                      {products.map(product => (
                        <div key={product.id} className="flex gap-3 p-3 border rounded-lg hover:border-orange-200 transition-colors">
                          <div className="w-16 h-16 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                            {product.image_url ? (
                              <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                            ) : (
                              <Package className="h-6 w-6 text-slate-400" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-sm text-slate-800 line-clamp-2">{product.name}</h4>
                            <div className="flex items-center gap-2 mt-1">
                              {product.brand && <span className="text-xs text-slate-500">{product.brand}</span>}
                              {product.oem_code && <span className="text-xs text-slate-400">OEM: {product.oem_code}</span>}
                            </div>
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-orange-600 font-bold">{formatCurrency(product.display_price || product.price)}</span>
                              <Button size="sm" onClick={() => addToCart(product)} className="bg-orange-500 hover:bg-orange-600 h-8">
                                <Plus className="h-4 w-4 mr-1" />
                                Sepete Ekle
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Cart Sidebar - Desktop */}
            <div className="hidden lg:block">
              <Card className="shadow-sm sticky top-40">
                <CardHeader className="border-b bg-slate-50">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <ShoppingCart className="h-5 w-5 text-orange-500" />
                    Sepetim ({cart.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  {cart.length === 0 ? (
                    <div className="text-center py-8 text-slate-400">
                      <ShoppingCart className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>Sepetiniz boş</p>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
                        {cart.map(item => (
                          <div key={item.id} className="flex gap-2 p-2 bg-slate-50 rounded-lg">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-800 line-clamp-1">{getName(item)}</p>
                              <div className="flex items-center justify-between mt-1">
                                <div className="flex items-center gap-1">
                                  <Button size="sm" variant="outline" className="h-6 w-6 p-0" onClick={() => updateQuantity(item.id, -1)}>
                                    <Minus className="h-3 w-3" />
                                  </Button>
                                  <span className="w-6 text-center text-sm">{item.quantity}</span>
                                  <Button size="sm" variant="outline" className="h-6 w-6 p-0" onClick={() => updateQuantity(item.id, 1)}>
                                    <Plus className="h-3 w-3" />
                                  </Button>
                                </div>
                                <span className="text-orange-600 font-semibold text-sm">{formatCurrency(getPrice(item) * item.quantity)}</span>
                              </div>
                            </div>
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500" onClick={() => removeFromCart(item.id)}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>

                      <div className="border-t pt-4 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500">Ara Toplam</span>
                          <span>{formatCurrency(cartTotal)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500">Kargo</span>
                          <span className={shippingData.is_free_shipping ? 'text-green-600 font-medium' : 'text-slate-800'}>{shippingData.is_free_shipping ? 'Ücretsiz Kargo' : formatCurrency(shippingCost)}</span>
                        </div>
                        <div className="flex justify-between font-bold text-lg pt-2 border-t">
                          <span>Toplam</span>
                          <span className="text-orange-600">{formatCurrency(grandTotal)}</span>
                        </div>
                      </div>

                      <Button onClick={() => setStep(2)} className="w-full mt-4 bg-orange-500 hover:bg-orange-600 h-12">
                        Devam Et
                        <ChevronRight className="ml-1 h-5 w-5" />
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
          
          {/* Mobile Bottom Cart Bar */}
          {cart.length > 0 && (
            <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t-2 border-orange-100 shadow-2xl z-50 p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <ShoppingCart className="h-5 w-5 text-orange-500" />
                    <span className="font-semibold text-slate-800">{cart.length} Ürün</span>
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-lg font-bold text-orange-600">{formatCurrency(grandTotal)}</span>
                    {shippingCost === 0 && (
                      <span className="text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Ücretsiz Kargo</span>
                    )}
                  </div>
                </div>
                <Button 
                  onClick={() => setStep(2)} 
                  className="bg-orange-500 hover:bg-orange-600 h-12 px-6 font-semibold"
                >
                  Devam Et
                  <ChevronRight className="ml-1 h-5 w-5" />
                </Button>
              </div>
            </div>
          )}
          <div className="lg:hidden h-28"></div>
          </>
        ) : (
          /* Step 2: Checkout Form */
          <div className="max-w-3xl mx-auto">
            <Button variant="ghost" onClick={() => setStep(1)} className="mb-6 text-slate-600">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Alışverişe Devam Et
            </Button>

            <Card className="shadow-sm">
              <CardHeader className="border-b bg-slate-50">
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="h-5 w-5 text-orange-500" />
                  Teslimat Bilgileri
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Contact Info */}
                <div className="space-y-4">
                  <h3 className="font-medium text-slate-800 flex items-center gap-2">
                    <User className="h-4 w-4 text-orange-500" />
                    İletişim Bilgileri
                  </h3>
                  <div>
                    <Label className="text-slate-700">Ad Soyad *</Label>
                    <Input
                      value={form.customer_name}
                      onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                      placeholder="Adınız Soyadınız"
                      className={`mt-1 ${errors.customer_name ? 'border-red-500' : ''}`}
                    />
                    {errors.customer_name && <p className="text-red-500 text-xs mt-1">{errors.customer_name}</p>}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-700">Telefon *</Label>
                      <div className="relative mt-1">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          value={form.customer_phone}
                          onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                          placeholder="05XX XXX XX XX"
                          className={`pl-10 ${errors.customer_phone ? 'border-red-500' : ''}`}
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-700">Email (opsiyonel)</Label>
                      <div className="relative mt-1">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                          type="email"
                          value={form.customer_email}
                          onChange={(e) => setForm({ ...form, customer_email: e.target.value })}
                          placeholder="email@ornek.com"
                          className="pl-10"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Address */}
                <div className="space-y-4 pt-4 border-t">
                  <h3 className="font-medium text-slate-800 flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-orange-500" />
                    Teslimat Adresi
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-700">İl *</Label>
                      <select
                        value={form.shipping_city}
                        onChange={(e) => setForm({ ...form, shipping_city: e.target.value })}
                        className="w-full mt-1 px-3 py-2 border rounded-md bg-white text-slate-800"
                      >
                        {TURKISH_CITIES.map(city => (
                          <option key={city} value={city}>{city}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-slate-700">İlçe</Label>
                      <Input
                        value={form.shipping_district}
                        onChange={(e) => setForm({ ...form, shipping_district: e.target.value })}
                        placeholder="İlçe adı"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-slate-700">Açık Adres *</Label>
                    <textarea
                      value={form.shipping_address}
                      onChange={(e) => setForm({ ...form, shipping_address: e.target.value })}
                      placeholder="Mahalle, sokak, bina no, daire no..."
                      rows={3}
                      className={`w-full mt-1 px-3 py-2 border rounded-md text-slate-800 ${errors.shipping_address ? 'border-red-500' : ''}`}
                    />
                    {errors.shipping_address && <p className="text-red-500 text-xs mt-1">{errors.shipping_address}</p>}
                  </div>
                </div>

                {/* Order Summary with Images */}
                <div className="bg-slate-50 rounded-xl p-4 md:p-6 border">
                  <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <ShoppingCart className="h-4 w-4 text-orange-500" />
                    Sepetim ({cart.length} Ürün)
                  </h3>
                  <div className="space-y-3 mb-4">
                    {cart.map(item => (
                      <div key={item.id} className="flex gap-3 p-3 bg-white rounded-lg border">
                        <div className="w-16 h-16 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {item.image_url ? (
                            <img src={item.image_url} alt={getName(item)} className="w-full h-full object-cover" />
                          ) : (
                            <Package className="h-6 w-6 text-slate-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-slate-800 line-clamp-2">{getName(item)}</p>
                          {item.brand && <p className="text-xs text-slate-500">{item.brand}</p>}
                          {item.oem_code && <p className="text-xs text-slate-400">OEM: {item.oem_code}</p>}
                          <div className="flex items-center justify-between mt-1">
                            <span className="text-orange-600 font-bold">{formatCurrency(getPrice(item))}</span>
                            <div className="flex items-center gap-1">
                              <Button size="sm" variant="outline" onClick={() => updateQuantity(item.id, -1)} className="h-7 w-7 p-0 rounded-full">
                                <Minus className="h-3 w-3" />
                              </Button>
                              <span className="w-6 text-center text-sm font-medium">{item.quantity}</span>
                              <Button size="sm" variant="outline" onClick={() => updateQuantity(item.id, 1)} className="h-7 w-7 p-0 rounded-full">
                                <Plus className="h-3 w-3" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => removeFromCart(item.id)} className="h-7 w-7 p-0 text-red-500 hover:bg-red-50 ml-1">
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-2 pt-2 border-t border-slate-300">
                    <div className="flex justify-between text-sm text-slate-600">
                      <span>Ara Toplam</span>
                      <span>{formatCurrency(cartTotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Kargo</span>
                      <span className={shippingData.is_free_shipping ? 'text-green-600 font-medium' : 'text-slate-800'}>{shippingData.is_free_shipping ? 'Ücretsiz Kargo' : formatCurrency(shippingCost)}</span>
                    </div>
                    {paymentMethod === 'credit_card' && selectedInstallment > 1 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-500">Taksit Farkı</span>
                        <span className="text-slate-500">+{formatCurrency(currentInstallment.total - grandTotal)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-lg pt-2 border-t border-slate-300">
                      <span className="text-slate-800">Toplam</span>
                      <span className="text-orange-600">{formatCurrency(paymentMethod === 'credit_card' ? currentInstallment.total : grandTotal)}</span>
                    </div>
                  </div>
                </div>

                {/* Payment Method Selection */}
                <div className="bg-white rounded-xl p-4 md:p-6 border">
                  <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-orange-500" />
                    Ödeme Yöntemi
                  </h3>
                  
                  <div className="space-y-3">
                    {/* Credit Card Option */}
                    <div 
                      onClick={() => setPaymentMethod('credit_card')}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${paymentMethod === 'credit_card' ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-slate-300'}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${paymentMethod === 'credit_card' ? 'border-orange-500' : 'border-slate-300'}`}>
                          {paymentMethod === 'credit_card' && <div className="w-2.5 h-2.5 rounded-full bg-orange-500" />}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-slate-800">Kredi Kartı / Banka Kartı</div>
                          <div className="text-xs text-slate-500">Taksitli ödeme imkanı</div>
                        </div>
                        <div className="flex gap-1">
                          <div className="w-8 h-5 bg-gradient-to-r from-red-500 to-yellow-500 rounded text-[6px] flex items-center justify-center text-white font-bold">MC</div>
                          <div className="w-8 h-5 bg-blue-700 rounded text-[6px] flex items-center justify-center text-white font-bold italic">VISA</div>
                        </div>
                      </div>
                    </div>


                  </div>

                  {/* Credit Card Form */}
                  {paymentMethod === 'credit_card' && (
                    <div className="mt-4 space-y-4 p-4 bg-slate-50 rounded-lg">
                      <div>
                        <Label className="text-slate-700">Kart Üzerindeki İsim</Label>
                        <Input
                          value={cardData.cardHolderName}
                          onChange={(e) => setCardData({ ...cardData, cardHolderName: e.target.value.toUpperCase() })}
                          placeholder="AD SOYAD"
                          className="mt-1 bg-white"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-700">Kart Numarası</Label>
                        <Input
                          value={cardData.cardNumber}
                          onChange={handleCardNumberChange}
                          placeholder="0000 0000 0000 0000"
                          className="mt-1 bg-white font-mono text-lg"
                          maxLength={19}
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <Label className="text-slate-700 text-xs">Ay</Label>
                          <select
                            value={cardData.expMonth}
                            onChange={(e) => setCardData({ ...cardData, expMonth: e.target.value })}
                            className="w-full mt-1 px-3 py-2 border rounded-md bg-white text-slate-800 h-10"
                            data-testid="card-exp-month"
                          >
                            <option value="">Ay</option>
                            {months.map((month) => (
                              <option key={month} value={month}>{month}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-slate-700 text-xs">Yıl</Label>
                          <select
                            value={cardData.expYear}
                            onChange={(e) => setCardData({ ...cardData, expYear: e.target.value })}
                            className="w-full mt-1 px-3 py-2 border rounded-md bg-white text-slate-800 h-10"
                            data-testid="card-exp-year"
                          >
                            <option value="">Yıl</option>
                            {years.map((year) => (
                              <option key={year} value={String(year)}>{year}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label className="text-slate-700 text-xs">CVV</Label>
                          <Input
                            type="password"
                            value={cardData.cvv}
                            onChange={(e) => setCardData({ ...cardData, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                            placeholder="***"
                            className="mt-1 bg-white"
                            maxLength={4}
                            data-testid="card-cvv"
                          />
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox id="use3D" checked={cardData.use3D} onCheckedChange={(checked) => setCardData({ ...cardData, use3D: checked })} />
                        <label htmlFor="use3D" className="text-sm text-slate-600">3D Secure ile güvenli ödeme</label>
                      </div>

                      {/* Installment Options */}
                      <div className="pt-4 border-t">
                        <Label className="text-slate-700 mb-3 block">Taksit Seçenekleri</Label>
                        <div className="grid grid-cols-4 gap-2">
                          {INSTALLMENT_OPTIONS.map((option) => {
                            const details = getInstallmentDetails(option.count);
                            const isSelected = selectedInstallment === option.count;
                            return (
                              <button
                                key={option.count}
                                type="button"
                                onClick={() => setSelectedInstallment(option.count)}
                                className={`relative p-2 rounded-lg border-2 transition-all ${isSelected ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-slate-300 bg-white'}`}
                              >
                                {isSelected && <div className="absolute -top-1 -right-1"><Check className="h-3 w-3 text-white bg-orange-500 rounded-full p-0.5" /></div>}
                                <div className="text-center">
                                  <div className={`font-bold text-xs ${isSelected ? 'text-orange-600' : 'text-slate-900'}`}>
                                    {option.count === 1 ? 'Peşin' : option.count}
                                  </div>
                                  <div className="text-[10px] text-slate-500 mt-0.5">{formatCurrency(details.monthly)}</div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                        {selectedInstallment > 1 && (
                          <div className="mt-3 bg-white rounded-lg p-3 border">
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-slate-600">{selectedInstallment} Taksit</span>
                              <div className="text-right">
                                <div className="font-bold text-orange-600">{formatCurrency(currentInstallment.monthly)} x {selectedInstallment}</div>
                                <div className="text-xs text-slate-500">Toplam: {formatCurrency(currentInstallment.total)}</div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 p-2 rounded-lg">
                        <Lock className="h-3 w-3" />
                        256-bit SSL şifreleme ile güvenli ödeme
                      </div>
                    </div>
                  )}
                </div>

                {/* Submit */}
                <div className="flex gap-4 pt-4">
                  <Button variant="outline" onClick={() => setStep(1)} className="flex-1 h-12">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Geri
                  </Button>
                  <Button onClick={handleSubmit} disabled={submitting} className="flex-1 bg-orange-500 hover:bg-orange-600 h-12 text-base">
                    {submitting ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <CheckCircle className="h-5 w-5 mr-2" />}
                    {`${formatCurrency(currentInstallment.total)} Öde`}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default GuestCheckoutPage;
